import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Banknote, Loader2, Lock } from 'lucide-react';

import api from '../../lib/api';
import { fmtCurrency } from '../../lib/localeUtils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { downloadSlip } from './HandoverRow';

// Ghana cedi notes and coins, largest first. Counts are the cashier's working;
// the counted total is what the variance is judged on.
const DENOMINATIONS = [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1];

const denomLabel = (value) => (value >= 1 ? `${value}` : `${Math.round(value * 100)}p`);

const Row = ({ label, value, muted, strong, testid }) => (
  <div className="flex items-center justify-between text-sm py-1" data-testid={testid}>
    <span className={muted ? 'text-muted-foreground' : ''}>{label}</span>
    <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>{value}</span>
  </div>
);

// Shown when a cashier locks the till: what the shift took, then a drawer count.
export const ShiftHandoverDialog = ({ open, onClose, onDone, lockMode = true }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/shift/summary');
      setSummary(data);
      setCounted(String(data.expected_cash ?? 0));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your shift totals');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    setCounts({});
    setNote('');
    load();
  }, [open, load]);

  const autoTotal = useMemo(
    () => DENOMINATIONS.reduce((sum, value) => sum + value * (Number(counts[value]) || 0), 0),
    [counts],
  );

  const setCount = (value, raw) => {
    const next = { ...counts, [value]: raw.replace(/\D/g, '') };
    setCounts(next);
    const total = DENOMINATIONS.reduce((sum, v) => sum + v * (Number(next[v]) || 0), 0);
    setCounted(total.toFixed(2));
  };

  const expected = Number(summary?.expected_cash || 0);
  const threshold = Number(summary?.variance_threshold ?? 0);
  const variance = Number(counted === '' ? NaN : Number(counted) - expected);
  const noteRequired = Number.isFinite(variance) && Math.abs(variance) > threshold;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/shift/handover', {
        counted_total: Number(counted),
        denominations: DENOMINATIONS.map((value) => ({ value, count: Number(counts[value]) || 0 })),
        note,
      });
      toast.success(lockMode ? 'Handover saved. Till locked.' : 'Handover saved.', {
        action: { label: 'Print slip', onClick: () => downloadSlip(data.handover_id, 'a4') },
        duration: 8000,
      });
      onDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the handover');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="shift-handover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-4 w-4 text-primary" /> Shift handover
          </DialogTitle>
          <DialogDescription>
            {loading ? 'Adding up your shift…' : (
              <>
                {summary?.terminal_label || 'This till'} · since{' '}
                {summary?.shift_start ? new Date(summary.shift_start).toLocaleString() : '—'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2" data-testid="shift-handover-takings">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">What this shift took</p>
              <div className="rounded-lg border divide-y">
                <div className="p-3">
                  {(summary?.payments || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="shift-handover-no-sales">
                      No sales on this till yet this shift.
                    </p>
                  ) : summary.payments.map((p) => (
                    <Row
                      key={p.method}
                      label={`${p.label} (${p.count})`}
                      value={fmtCurrency(p.total)}
                      testid={`shift-pay-${p.method}`}
                    />
                  ))}
                </div>
                <div className="p-3">
                  <Row label="Transactions" value={summary?.sales_count ?? 0} muted testid="shift-txn-count" />
                  <Row label="Gross sales" value={fmtCurrency(summary?.gross_sales || 0)} testid="shift-gross" />
                  <Row label="Refunds" value={`− ${fmtCurrency(summary?.refunds_total || 0)}`} testid="shift-refunds" />
                </div>
                <div className="p-3 bg-muted/40 rounded-b-lg">
                  <Row label="Opening float" value={fmtCurrency(summary?.opening_float || 0)} muted testid="shift-opening-float" />
                  <Row label="Cash taken" value={fmtCurrency(summary?.cash_sales || 0)} muted testid="shift-cash-sales" />
                  <Row label="Cash refunded" value={`− ${fmtCurrency(summary?.cash_refunds || 0)}`} muted testid="shift-cash-refunds" />
                  <Row label="Expected in drawer" value={fmtCurrency(expected)} strong testid="shift-expected-cash" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Count the drawer</p>
              <div className="grid grid-cols-2 gap-2">
                {DENOMINATIONS.map((value) => (
                  <div key={value} className="flex items-center gap-2">
                    <span className="w-12 text-xs text-muted-foreground tabular-nums">{denomLabel(value)}</span>
                    <Input
                      inputMode="numeric" className="h-9" placeholder="0"
                      value={counts[value] ?? ''}
                      onChange={(e) => setCount(value, e.target.value)}
                      data-testid={`denom-${String(value).replace('.', '-')}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="denom-auto-total">
                Counted from the grid: {fmtCurrency(autoTotal)}
              </p>

              <div className="space-y-1.5">
                <Label className="text-xs">Total cash counted (you can override)</Label>
                <Input
                  inputMode="decimal" value={counted}
                  onChange={(e) => setCounted(e.target.value.replace(/[^\d.]/g, ''))}
                  data-testid="counted-total-input"
                />
              </div>

              <div
                className={`rounded-lg px-3 py-2 text-sm flex items-center justify-between ${
                  !Number.isFinite(variance) ? 'bg-muted'
                    : Math.abs(variance) <= threshold ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                    : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                }`}
                data-testid="shift-variance"
              >
                <span>
                  {!Number.isFinite(variance) ? 'Enter what you counted'
                    : variance === 0 ? 'Drawer balances'
                    : variance > 0 ? 'Over by' : 'Short by'}
                </span>
                {Number.isFinite(variance) && variance !== 0 && (
                  <span className="font-semibold tabular-nums">{fmtCurrency(Math.abs(variance))}</span>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Note {noteRequired ? <span className="text-amber-600">(required — over {fmtCurrency(threshold)} out)</span> : '(optional)'}
                </Label>
                <Textarea
                  rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. GH₵20 float moved to the safe"
                  data-testid="shift-note-input"
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="shift-handover-cancel">Cancel</Button>
          {lockMode && (
            <Button variant="outline" onClick={onDone} className="gap-2" data-testid="shift-lock-without-count">
              <Lock className="h-4 w-4" /> Lock without counting
            </Button>
          )}
          <Button
            onClick={save}
            disabled={busy || loading || counted === '' || (noteRequired && !note.trim())}
            className="gap-2" data-testid="shift-handover-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {lockMode ? <>Save &amp; lock till</> : 'Save handover'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
